import { useState, useEffect } from "react";
import { Menu, X, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const {
    theme,
    setTheme
  } = useTheme();
  const navigate = useNavigate();
  useEffect(() => {
    setMounted(true);
  }, []);
  const navItems = [{
    label: "Our Process",
    href: "/process"
  }, {
    label: "ROI Calculator",
    href: "#calculator"
  }, {
    label: "Pricing",
    href: "#packages"
  }, {
    label: "FAQ",
    href: "#faq"
  }, {
    label: "Blog",
    href: "/blog"
  }, {
    label: "About",
    href: "/about"
  }, {
    label: "Contact",
    href: "/contact"
  }];
  const handleNavigation = (href: string) => {
    if (href.startsWith("#")) {
      // For hash links, navigate to home first if not already there
      const currentPath = window.location.pathname;
      if (currentPath !== "/") {
        navigate("/");
        // Wait for navigation then scroll
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Already on home page, just scroll
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else {
      navigate(href);
    }
    setIsMenuOpen(false);
  };
  if (!mounted) return null;
  return <nav className="fixed top-0 left-0 right-0 z-50 nav-glass">{/* Enhanced glass navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Enhanced Logo */}
          <div className="flex-shrink-0">
            <Link to="/">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-display font-black bg-gradient-brand bg-clip-text text-transparent relative">
                Contencraft
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full animate-pulse"></div>
              </h1>
            </Link>
          </div>

          {/* Tablet/Desktop Navigation - Improved spacing and breakpoints */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map(item => <button key={item.label} onClick={() => handleNavigation(item.href)} className="text-muted-foreground hover:text-primary font-medium transition-all duration-300 hover:scale-105 relative group whitespace-nowrap">
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </button>)}
          </div>

          {/* Tablet Navigation - Compact version for medium screens */}
          <div className="hidden md:flex lg:hidden items-center space-x-1">
            {navItems.slice(0, 4).map(item => <button key={item.label} onClick={() => handleNavigation(item.href)} className="text-muted-foreground hover:text-primary font-medium transition-all duration-300 text-sm px-2 py-1 rounded relative group whitespace-nowrap">
                {item.label === "Our Process" ? "Process" : 
                 item.label === "ROI Calculator" ? "ROI" : 
                 item.label === "Pricing" ? "Price" : 
                 item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
              </button>)}
          </div>

          {/* Theme Toggle & CTA - Responsive */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="w-9 h-9 p-0">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="hero" size="sm" onClick={() => handleNavigation("/contact")} className="btn-interactive text-sm font-bold px-4 whitespace-nowrap">Book Strategy Call</Button>
          </div>

          {/* Tablet CTA - Compact version */}
          <div className="hidden md:flex lg:hidden items-center space-x-2">
            <Button variant="ghost" size="sm" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="w-8 h-8 p-0">
              {theme === "dark" ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
            </Button>
            <Button variant="hero" size="sm" onClick={() => handleNavigation("/contact")} className="btn-interactive text-xs font-bold px-3 py-2 whitespace-nowrap">Book Call</Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            <Button variant="ghost" size="sm" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="w-9 h-9 p-0">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsMenuOpen(!isMenuOpen)} className="w-9 h-9 p-0">
              {isMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Enhanced Mobile/Tablet Menu */}
        {isMenuOpen && <div className="md:hidden border-t border-border glass-panel animate-slide-up">{/* Enhanced mobile menu */}
            <div className="px-3 pt-3 pb-4 space-y-2">
              {navItems.map(item => <button key={item.label} onClick={() => handleNavigation(item.href)} className="block w-full text-left px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-all duration-200 font-medium">
                  {item.label}
                </button>)}
              <div className="px-4 py-3 pt-4 border-t border-border/50">
                <Button variant="hero" size="sm" onClick={() => handleNavigation("/contact")} className="w-full font-bold">
                  Build Your Team
                </Button>
              </div>
            </div>
          </div>}
      </div>
    </nav>;
};
export default Navigation;